/**
 * Persistence contracts.
 *
 * Gameplay code depends on these interfaces and never on IndexedDB, fetch, or
 * any storage detail. Swapping the local prototype store for a server-backed one
 * is a matter of constructing a different implementation at startup; nothing
 * above this layer changes.
 *
 * The persistence model is "seed plus delta". The base universe is reproducible
 * from seeds, so nothing stores terrain vertices or rock positions. What is
 * stored is only what the player changed.
 */

export interface TerrainModification {
  /** Planet-fixed direction of the modification, unit vector components. */
  direction: [number, number, number];
  /** Radius of effect, metres. */
  radius: number;
  /** Height delta applied, metres. */
  delta: number;
}

export interface ChunkDelta {
  terrainModifications: TerrainModification[];
  discoveredLocationIds: string[];
  destroyedObjectIds: string[];
}

export function emptyChunkDelta(): ChunkDelta {
  return {
    terrainModifications: [],
    discoveredLocationIds: [],
    destroyedObjectIds: [],
  };
}

export interface ShipRecord {
  defId: string;
  /** Reference body whose inertial frame the state is expressed in. */
  frameBodyId: string;
  position: [number, number, number];
  velocity: [number, number, number];
  orientation: [number, number, number, number];
  angularVelocity: [number, number, number];
  tanks: [string, number][];
  hullDamage: number;
  gearDamage: number;
  wingDamage: number;
  fieldDriveTemperature: number;
  fieldDriveDamage: number;
  batteryCharge: number;
  hullTemperature: number;
}

export interface PlayerRecord {
  /** Where the player is: 'ship', or a body id when on foot. */
  location: 'ship' | string;
  /** Planet-fixed position when on foot. */
  surfacePosition?: [number, number, number];
  visitedBodies: string[];
}

export interface SaveMetadata {
  id: string;
  name: string;
  seed: number;
  /** Simulation time, seconds. */
  simulationTime: number;
  /** Wall-clock time the save was written. */
  savedAt: number;
  version: number;
}

export interface SaveGame {
  metadata: SaveMetadata;
  player: PlayerRecord;
  ship: ShipRecord;
  settings: Record<string, unknown>;
}

/**
 * Storage for procedural-world deltas, keyed by planet and chunk.
 * Implementations must be safe to call concurrently.
 */
export interface WorldRepository {
  loadChunkDelta(planetId: string, chunkId: string): Promise<ChunkDelta | null>;
  saveChunkDelta(planetId: string, chunkId: string, delta: ChunkDelta): Promise<void>;
  /** Every delta for a planet, used when materialising a region. */
  loadPlanetDeltas(planetId: string): Promise<Map<string, ChunkDelta>>;
  clearPlanet(planetId: string): Promise<void>;
}

/** Storage for player, ship and progression state. */
export interface SaveRepository {
  list(): Promise<SaveMetadata[]>;
  load(id: string): Promise<SaveGame | null>;
  save(save: SaveGame): Promise<void>;
  delete(id: string): Promise<void>;
}

/** Storage for user settings, kept separate from saves. */
export interface SettingsRepository {
  load(): Promise<Record<string, unknown>>;
  save(settings: Record<string, unknown>): Promise<void>;
}

export const SAVE_VERSION = 2;
